// « Le studio » (#/studio?r=actus | r=infos) — hub Studio of the navigation refonte 21/10 (spec ARBORESCENCE-ACS.md §3, plan
// Task 6). The rubrique is read from the URL (useRubrique); the local dock (Actus · Infos pratiques) belongs to the shell.
// - Actus: « À la une » (latest news), the upcoming studio events (detail + registration in a sheet), then every other news.
//   A news opens as an addressable detail page, #/studio?r=actus&actu=<id> (format of the ⌘K search); the dock stays on Actus.
// - Infos pratiques: the real team (Alexandre Simonis, Antoine Chapalain, official portraits), today's status and the weekly
//   hours, the address with the route detail, the known closures and the studio contact.
// Fixtures are read only (studio-fixtures.ts is shared). Nothing is sent: contact and route open prototype details; a coach
// contact opens their real thread of the prototype messaging. Styles: hub-studio.css (scope .hub-std).
import { Button } from "@ubm/ui/components/button";
import { useEffect, useState, type ReactNode } from "react";
import { Check, ChevronLeft, ChevronRight, Clock, MapPin, MessageCircle, Navigation } from "lucide-react";
import { StatusBar, type Route } from "./parts";
import { CustomSheet } from "./sheet";
import { ADDRESS, CLOSURES, CONTACT, EVENTS, HOURS, HOURS_SOURCE, NEWS, TEAM, TEAM_SOURCE, type Coach, type News, type StudioEvent } from "./studio-fixtures";
import { HubHead, ShellActions } from "./shell";
import { useHash, useRubrique } from "./domains";
import { DEMO_TIME, DEMO_TODAY } from "./demo-context";
import "./hub-studio.css";

interface StudioProps { go: (route: Route) => void; onOutOfScope: (label: string) => void }
type Sheet = { kind: "event"; e: StudioEvent } | { kind: "coach"; c: Coach } | { kind: "contact"; to: string } | { kind: "itineraire" };

/** Session memory of event registrations (local only). */
const registered = new Set<string>();

// Origin of a news detail: the hash we came from (⌘K from another domain, home widget…), so « retour » goes back there.
let detailOrigin: string | null = null;
if (typeof window !== "undefined") window.addEventListener("hashchange", e => {
  const next = new URL(e.newURL).hash, prev = new URL(e.oldURL).hash;
  if (/[?&]actu=/.test(next) && !/[?&]actu=/.test(prev)) detailOrigin = prev || null;
});

const COACH_FIL: Record<string, string> = { "c-alexandre": "alexandre", "c-antoine": "antoine" };
const firstName = (full: string) => full.split(" ")[0] ?? full;

/** « Sam. 24 oct. » → weekday / day / month for the date tile; the raw label stays the accessible text. */
function dateParts(label: string) {
  const [wd = "", day = "", month = ""] = label.split(" ");
  return { wd: wd.replace(".", ""), day, month: month.replace(".", "") };
}

/** Today's opening status from the demo clock (Monday 12/10, 16 h) and the weekly hours. */
function todayStatus() {
  const idx = (new Date(`${DEMO_TODAY}T12:00:00`).getDay() + 6) % 7; // Monday = 0
  const h = HOURS[idx];
  if (!h) return null;
  const m = h.hours.match(/(\d+)\s*h\s*–\s*(\d+)\s*h/);
  const now = Number(DEMO_TIME.split(":")[0]);
  const open = !!m && now >= Number(m[1]) && now < Number(m[2]);
  return { idx, open, label: open ? `Ouvert · jusqu'à ${m![2]} h` : m ? `Fermé · ouvre à ${m[1]} h` : "Fermé aujourd'hui" };
}

function StudioSheet({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  // Escape, focus trap / return, inert background, grip: the shared engine through CustomSheet.
  return <CustomSheet title={title} onClose={onClose} overlayClassName="cc-studio-overlay" className="cc-studio-sheet">
    {children}
    <p className="cc-home-demo">Prototype : contenu de démonstration, rien n'est envoyé.</p>
  </CustomSheet>;
}

export function Studio({ go }: StudioProps) {
  const rub = useRubrique("studio");
  const hash = useHash();
  const actuId = hash.match(/[?&]actu=([\w-]+)/)?.[1];
  const actu = rub === "actus" && actuId ? NEWS.find(n => n.id === actuId) ?? null : null;
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [, bump] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => { if (!toast) return; const t = window.setTimeout(() => setToast(null), 2200); return () => window.clearTimeout(t); }, [toast]);
  // A new view starts at its top (rubrique switch from the dock, detail opened or closed).
  useEffect(() => { document.querySelector(".hub-std .cc-scroll")?.scrollTo({ top: 0 }); }, [rub, actuId]);
  const close = () => setSheet(null);
  const participate = (e: StudioEvent) => { registered.add(e.id); bump(x => x + 1); close(); setToast(`Inscrit · ${e.title}`); };
  const openActu = (n: News) => { window.location.hash = `#/studio?r=actus&actu=${n.id}`; };
  const back = () => {
    if (actu) { const o = detailOrigin; detailOrigin = null; window.location.hash = o && o !== hash ? o : "#/studio?r=actus"; }
    else go("accueil");
  };
  const contactCoach = (c: Coach) => { const fil = COACH_FIL[c.id]; close(); if (fil) window.location.hash = `#/messages?fil=${fil}`; else setSheet({ kind: "contact", to: firstName(c.firstName) }); };

  return <div className="cc-screen cc-studio hub-std">
    <StatusBar />
    <div className="cc-scroll">
      <HubHead domain="studio" title={actu ? "Actu" : undefined} back={actu ? "Retour aux actus" : undefined} onBack={back} />

      <div className="hub-std-view" key={actu ? `a-${actu.id}` : rub}>
        {actu ? <ActuDetail n={actu} onOpen={openActu} />
          : rub === "infos" ? <Infos onCoach={c => setSheet({ kind: "coach", c })} onRoute={() => setSheet({ kind: "itineraire" })} onContact={() => setSheet({ kind: "contact", to: "ACS Studio" })} />
          : <Actus onActu={openActu} onEvent={e => setSheet({ kind: "event", e })} />}
      </div>
    </div>
    {toast && <div className="cc-studio-toast hub-std-toast" role="status">{toast}</div>}

    {sheet?.kind === "event" && <StudioSheet title={sheet.e.title} onClose={close}>
      {sheet.e.photo && <img className="cc-studio-hero" src={sheet.e.photo} alt="" />}
      <p className="cc-home-p">{sheet.e.description}</p>
      <ul className="cc-suivi-kv"><li><span>Date</span><b>{sheet.e.date}</b></li><li><span>Horaire</span><b>{sheet.e.time}</b></li><li><span>Lieu</span><b>{sheet.e.place}</b></li></ul>
      {registered.has(sheet.e.id) ? <p className="cc-studio-ok"><Check size={14} strokeWidth={2.6} /> Inscrit</p>
        : sheet.e.registration === "open" ? <Button variant="primary" size="lg" className="cc-cta" onClick={() => participate(sheet.e)}>Je participe</Button>
        : <p className="cc-studio-meta">Les inscriptions ouvriront bientôt.</p>}
    </StudioSheet>}
    {sheet?.kind === "coach" && <StudioSheet title={sheet.c.firstName} onClose={close}>
      <img className="cc-studio-hero cc-studio-portrait-hero" src={sheet.c.photo} alt={sheet.c.alt} />
      <p className="cc-studio-meta">{sheet.c.role}{sheet.c.mine ? " · ton coach (affectation de démo)" : ""}</p>
      {sheet.c.intro.map(p => <p key={p} className="cc-home-p">{p}</p>)}
      {sheet.c.specialties.length > 0 && <div className="cc-chips cc-studio-chips">{sheet.c.specialties.map(s => <span key={s} className="cc-chip">{s}</span>)}</div>}
      <p className="cc-home-demo">{TEAM_SOURCE}</p>
      <Button variant="primary" size="lg" className="cc-cta" onClick={() => contactCoach(sheet.c)}><MessageCircle size={16} strokeWidth={2} /> Écrire à {firstName(sheet.c.firstName)}</Button>
    </StudioSheet>}
    {sheet?.kind === "contact" && <StudioSheet title={`Contacter ${sheet.to}`} onClose={close}>
      <p className="cc-suivi-row-title">{CONTACT.channel}</p>
      <p className="cc-home-p">{CONTACT.note}</p>
    </StudioSheet>}
    {sheet?.kind === "itineraire" && <StudioSheet title="Itinéraire" onClose={close}>
      <p className="cc-home-p">Dans l'app réelle, l'itinéraire vers {ADDRESS.line}, {ADDRESS.city} s'ouvrirait dans ton application de cartes.</p>
      <p className="cc-studio-meta">Aucune application externe n'est ouverte depuis ce prototype.</p>
    </StudioSheet>}
  </div>;
}

/* ---------------- Actus ---------------- */
function Actus({ onActu, onEvent }: { onActu: (n: News) => void; onEvent: (e: StudioEvent) => void }) {
  const [une, ...rest] = NEWS;
  if (!une && EVENTS.length === 0) return <p className="hub-std-empty">Aucune actualité pour le moment. Les nouvelles du studio arriveront ici.</p>;
  return <>
    {une && <section aria-labelledby="std-une">
      <h2 id="std-une" className="hub-std-kicker">À la une</h2>
      <button type="button" className="hub-std-une" onClick={() => onActu(une)}>
        {une.photo && <span className="hub-std-une-media"><img src={une.photo} alt="" /></span>}
        <span className="hub-std-une-body">
          <span className="hub-std-date">{une.date}</span>
          <span className="hub-std-une-title">{une.title}</span>
          <span className="hub-std-excerpt">{une.excerpt}</span>
          <span className="hub-std-more">Lire <ChevronRight size={14} strokeWidth={2.4} /></span>
        </span>
      </button>
    </section>}

    {EVENTS.length > 0 && <section aria-labelledby="std-rdv">
      <h2 id="std-rdv" className="hub-std-kicker">Rendez-vous au studio</h2>
      <ul className="hub-std-events">{EVENTS.map(e => { const d = dateParts(e.date), done = registered.has(e.id); return <li key={e.id}>
        <button type="button" className="hub-std-event" onClick={() => onEvent(e)} aria-label={`${e.title}, ${e.date}, ${e.time}`}>
          <span className="hub-std-tile" aria-hidden="true"><small>{d.wd}</small><b>{d.day}</b><small>{d.month}</small></span>
          <span className="hub-std-event-text">
            <span className="hub-std-event-title">{e.title}</span>
            <span className="hub-std-meta">{e.time} · {e.place}</span>
            <span className={`hub-std-state${done ? " is-done" : e.registration === "open" ? " is-open" : ""}`}>
              {done ? <><Check size={12} strokeWidth={2.8} /> Inscrit</> : e.registration === "open" ? "Inscriptions ouvertes" : "Inscriptions bientôt"}
            </span>
          </span>
          <ChevronRight className="hub-std-chev" size={16} strokeWidth={2} />
        </button>
      </li>; })}</ul>
    </section>}

    {rest.length > 0 && <section aria-labelledby="std-fil">
      <h2 id="std-fil" className="hub-std-kicker">Les dernières nouvelles</h2>
      <ul className="hub-std-feed">{rest.map(n => <li key={n.id}>
        <button type="button" className="hub-std-feed-row" onClick={() => onActu(n)}>
          <span className="hub-std-feed-text">
            <span className="hub-std-date">{n.date}</span>
            <span className="hub-std-feed-title">{n.title}</span>
            <span className="hub-std-meta">{n.excerpt}</span>
          </span>
          {n.photo ? <img className="hub-std-thumb" src={n.photo} alt="" /> : <ChevronRight className="hub-std-chev" size={16} strokeWidth={2} />}
        </button>
      </li>)}</ul>
    </section>}
  </>;
}

function ActuDetail({ n, onOpen }: { n: News; onOpen: (n: News) => void }) {
  const others = NEWS.filter(x => x.id !== n.id).slice(0, 2);
  return <article className="hub-std-article" aria-labelledby="std-art">
    {n.photo && <img className="hub-std-article-hero" src={n.photo} alt="" />}
    <p className="hub-std-date">Actualité · {n.date}</p>
    <h2 id="std-art" className="hub-std-article-title">{n.title}</h2>
    <div className="hub-std-article-body">{n.body.map(p => <p key={p}>{p}</p>)}</div>
    <p className="cc-home-demo">Prototype : actualité de démonstration.</p>
    {others.length > 0 && <section aria-labelledby="std-aussi" className="hub-std-aussi">
      <h3 id="std-aussi" className="hub-std-kicker">À lire aussi</h3>
      <ul className="hub-std-feed">{others.map(o => <li key={o.id}>
        <button type="button" className="hub-std-feed-row" onClick={() => onOpen(o)}>
          <span className="hub-std-feed-text"><span className="hub-std-date">{o.date}</span><span className="hub-std-feed-title">{o.title}</span></span>
          {o.photo ? <img className="hub-std-thumb" src={o.photo} alt="" /> : <ChevronRight className="hub-std-chev" size={16} strokeWidth={2} />}
        </button>
      </li>)}</ul>
    </section>}
  </article>;
}

/* ---------------- Infos pratiques ---------------- */
function Infos({ onCoach, onRoute, onContact }: { onCoach: (c: Coach) => void; onRoute: () => void; onContact: () => void }) {
  const st = todayStatus();
  return <>
    {TEAM.length > 0 && <section aria-labelledby="std-eq">
      <h2 id="std-eq" className="hub-std-kicker">L'équipe</h2>
      <ul className="hub-std-team">{TEAM.map(c => <li key={c.id}>
        <button type="button" className="hub-std-coach" onClick={() => onCoach(c)} aria-label={`${c.firstName}, ${c.role}${c.mine ? ", ton coach" : ""}`}>
          <span className="hub-std-portrait"><img src={c.photo} alt="" />{c.mine && <span className="hub-std-mine">Ton coach</span>}</span>
          <span className="hub-std-coach-name">{c.firstName}</span>
          <span className="hub-std-meta">{c.role}</span>
        </button>
      </li>)}</ul>
      <p className="cc-home-demo hub-std-note">Affectation « ton coach » : configuration de démo.</p>
    </section>}

    <section aria-labelledby="std-h">
      <h2 id="std-h" className="hub-std-kicker">Horaires</h2>
      <div className="hub-std-panel">
        {st && <p className={`hub-std-now${st.open ? " is-open" : ""}`}><Clock size={16} strokeWidth={2} /> {st.label}</p>}
        <ul className="hub-std-hours">{HOURS.map((h, i) => <li key={h.day} aria-current={st?.idx === i ? "date" : undefined}>
          <span>{h.day}</span><b className={h.hours === "Fermé" ? "is-closed" : ""}>{h.hours}</b>
        </li>)}</ul>
        {CLOSURES.length > 0 && <div className="hub-std-closures">{CLOSURES.map(c => <p key={c.title}><b>{c.title}</b>{c.text} <span className="hub-std-meta">(démo)</span></p>)}</div>}
      </div>
      <p className="cc-home-demo hub-std-note">{HOURS_SOURCE}</p>
    </section>

    <section aria-labelledby="std-adr">
      <h2 id="std-adr" className="hub-std-kicker">Venir au studio</h2>
      <div className="hub-std-panel hub-std-venue">
        <p className="hub-std-address"><MapPin size={18} strokeWidth={1.9} /><span><b>ACS Studio</b>{ADDRESS.line}<br />{ADDRESS.city}</span></p>
        <div className="hub-std-actions">
          <Button variant="primary" size="lg" className="cc-cta" onClick={onRoute}><Navigation size={16} strokeWidth={2} /> Itinéraire</Button>
          <Button variant="secondary" size="lg" className="cc-cta" onClick={onContact}><MessageCircle size={16} strokeWidth={2} /> Contacter ACS</Button>
        </div>
      </div>
      <p className="cc-home-demo hub-std-note">Adresse : {ADDRESS.source}.</p>
    </section>
  </>;
}
